import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { differenceInDays } from 'date-fns';
import { parseLocalDate } from '../../../utils/dateUtils';
import { toDateInputValue } from '../../../utils/dateUtils';

const PHASE_STATUS_OPTIONS = [
  { value: 'Planning', label: 'Planning' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
  { value: 'On Hold', label: 'On Hold' },
];

/**
 * EditPhaseModal
 * Props:
 *  - isOpen: bool
 *  - mode: 'add' | 'edit'
 *  - phase: existing phase object (for edit mode)
 *  - projectId: string
 *  - phases: all phases array
 *  - deliverables: all deliverables array
 *  - onSave: async (formData, options) => void
 *  - onDelete: async (phaseId, orphanAction) => void
 *  - onClose: () => void
 *  - saving: bool
 */
const EditPhaseModal = ({
  isOpen,
  mode = 'add',
  phase,
  projectId,
  phases = [],
  deliverables = [],
  onSave,
  onDelete,
  onClose,
  saving = false,
}) => {
  const [form, setForm] = useState({
    name: '',
    startDate: '',
    endDate: '',
    status: 'Planning',
  });
  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orphanAction, setOrphanAction] = useState('unassign'); // 'unassign' | 'delete'
  const [shiftFuturePhases, setShiftFuturePhases] = useState(true);
  const [shiftChildren, setShiftChildren] = useState(true);
  const [deleting, setDeleting] = useState(false);

  // Compute children of this phase
  const phaseDeliverables = phase
    ? deliverables?.filter(d => d?.phaseId === phase?.id && (!d?.rowType || d?.rowType === 'deliverable'))
    : [];
  const hasChildren = phaseDeliverables?.length > 0;

  // Compute future phases (phases that start after this phase's start date)
  const futurePhasesCount = phase
    ? phases?.filter(p => p?.id !== phase?.id && p?.startDate && phase?.startDate && parseLocalDate(p?.startDate) > parseLocalDate(phase?.startDate))?.length
    : 0;

  // Date shift delta (days) when editing
  const computeDateShift = () => {
    if (mode !== 'edit' || !phase?.startDate || !form?.startDate) return 0;
    return differenceInDays(parseLocalDate(form?.startDate), parseLocalDate(phase?.startDate));
  };

  const dateShiftDays = computeDateShift();

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && phase) {
      setForm({
        name: phase?.name || '',
        startDate: toDateInputValue(phase?.startDate),
        endDate: toDateInputValue(phase?.endDate),
        status: phase?.status || 'Planning',
      });
    } else {
      setForm({ name: '', startDate: '', endDate: '', status: 'Planning' });
    }
    setErrors({});
    setShowDeleteConfirm(false);
    setOrphanAction('unassign');
    setShiftFuturePhases(true);
    setShiftChildren(true);
  }, [isOpen, mode, phase]);

  if (!isOpen) return null;

  const validate = () => {
    const errs = {};
    if (!form?.name?.trim()) errs.name = 'Phase name is required';
    if (!form?.startDate) errs.startDate = 'Start date is required';
    if (!form?.endDate) errs.endDate = 'End date is required';
    if (form?.startDate && form?.endDate && parseLocalDate(form?.startDate) > parseLocalDate(form?.endDate))
      errs.endDate = 'End date must be on or after start date';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    await onSave(
      {
        name: form?.name?.trim(),
        startDate: form?.startDate,
        endDate: form?.endDate,
        status: form?.status,
        projectId,
      },
      {
        shiftFuturePhases,
        shiftChildren,
        dateShiftDays,
        originalPhase: phase,
      }
    );
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(phase?.id, orphanAction);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const isEdit = mode === 'edit';

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-xl shadow-xl p-6 w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="GitBranch" size={18} className="text-accent" />
            <h3 className="text-base font-semibold text-foreground">
              {isEdit ? 'Edit Phase' : 'Add New Phase'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={saving || deleting}
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {!showDeleteConfirm ? (
          <>
            {/* Form */}
            <div className="space-y-3">
              {/* Phase Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Phase Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form?.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e?.target?.value }))}
                  placeholder="Enter phase name"
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent ${errors?.name ? 'border-red-400' : 'border-border'}`}
                />
                {errors?.name && <p className="text-xs text-red-500 mt-1">{errors?.name}</p>}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form?.startDate}
                    onChange={e => setForm(prev => ({ ...prev, startDate: e?.target?.value }))}
                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent ${errors?.startDate ? 'border-red-400' : 'border-border'}`}
                  />
                  {errors?.startDate && <p className="text-xs text-red-500 mt-1">{errors?.startDate}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form?.endDate}
                    min={form?.startDate}
                    onChange={e => setForm(prev => ({ ...prev, endDate: e?.target?.value }))}
                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent ${errors?.endDate ? 'border-red-400' : 'border-border'}`}
                  />
                  {errors?.endDate && <p className="text-xs text-red-500 mt-1">{errors?.endDate}</p>}
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                <select
                  value={form?.status}
                  onChange={e => setForm(prev => ({ ...prev, status: e?.target?.value }))}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                >
                  {PHASE_STATUS_OPTIONS?.map(opt => (
                    <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                  ))}
                </select>
              </div>

              {/* Edit-mode cascade options */}
              {isEdit && (
                <div className="border border-border rounded-lg p-3 bg-muted/30 space-y-2">
                  <p className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5">
                    <Icon name="Settings2" size={13} className="text-accent" />
                    Date Change Options
                  </p>

                  {/* Date shift preview */}
                  {dateShiftDays !== 0 && (
                    <div className={`text-xs px-2 py-1 rounded flex items-center gap-1.5 ${
                      dateShiftDays > 0 ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}>
                      <Icon name={dateShiftDays > 0 ? 'ArrowRight' : 'ArrowLeft'} size={11} />
                      Start date shifted by {Math.abs(dateShiftDays)} day{Math.abs(dateShiftDays) !== 1 ? 's' : ''} {dateShiftDays > 0 ? 'later' : 'earlier'}
                    </div>
                  )}

                  {/* Shift children */}
                  {hasChildren && dateShiftDays !== 0 && (
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={shiftChildren}
                        onChange={e => setShiftChildren(e?.target?.checked)}
                        className="mt-0.5 w-3.5 h-3.5 rounded border-border"
                      />
                      <div>
                        <span className="text-xs font-medium text-foreground">
                          Shift deliverables, tasks &amp; sub-tasks
                        </span>
                        <p className="text-xs text-muted-foreground">
                          Move all {phaseDeliverables?.length} deliverable{phaseDeliverables?.length !== 1 ? 's' : ''} (and their tasks/sub-tasks) by {Math.abs(dateShiftDays)} day{Math.abs(dateShiftDays) !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </label>
                  )}

                  {/* Shift future phases */}
                  {futurePhasesCount > 0 && dateShiftDays !== 0 && (
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={shiftFuturePhases}
                        onChange={e => setShiftFuturePhases(e?.target?.checked)}
                        className="mt-0.5 w-3.5 h-3.5 rounded border-border"
                      />
                      <div>
                        <span className="text-xs font-medium text-foreground">
                          Shift future phases
                        </span>
                        <p className="text-xs text-muted-foreground">
                          Also shift {futurePhasesCount} later phase{futurePhasesCount !== 1 ? 's' : ''} and their contents by the same amount
                        </p>
                      </div>
                    </label>
                  )}

                  {dateShiftDays === 0 && (
                    <p className="text-xs text-muted-foreground">No date shift — cascade options will appear when you change the start date.</p>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-5">
              <div>
                {isEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    iconName="Trash2"
                    iconPosition="left"
                    disabled={saving}
                  >
                    Delete Phase
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  iconName={saving ? 'Loader2' : 'Save'}
                  iconPosition="left"
                >
                  {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Phase'}
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* Delete confirmation */
          (<>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 flex-shrink-0">
                <Icon name="Trash2" size={20} className="text-red-600" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">Delete "{phase?.name}"?</h4>
                <p className="text-xs text-muted-foreground">This action cannot be undone.</p>
              </div>
            </div>
            {hasChildren && (
              <div className="mb-4 border border-border rounded-lg p-3 bg-muted/30 space-y-2">
                <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Icon name="AlertTriangle" size={13} className="text-amber-500" />
                  This phase has {phaseDeliverables?.length} deliverable{phaseDeliverables?.length !== 1 ? 's' : ''} assigned
                </p>
                <p className="text-xs text-muted-foreground mb-2">What should happen to the deliverables, tasks, and sub-tasks?</p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="orphanAction"
                    value="unassign"
                    checked={orphanAction === 'unassign'}
                    onChange={() => setOrphanAction('unassign')}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="text-xs font-medium text-foreground">Unassign from phase</span>
                    <p className="text-xs text-muted-foreground">Keep deliverables but move them to "Unassigned"</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="orphanAction"
                    value="delete"
                    checked={orphanAction === 'delete'}
                    onChange={() => setOrphanAction('delete')}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="text-xs font-medium text-foreground text-red-600">Delete all deliverables, tasks &amp; sub-tasks</span>
                    <p className="text-xs text-muted-foreground">Permanently remove all items in this phase</p>
                  </div>
                </label>
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={deleting}
                iconName={deleting ? 'Loader2' : 'Trash2'}
                iconPosition="left"
              >
                {deleting ? 'Deleting…' : 'Delete Phase'}
              </Button>
            </div>
          </>)
        )}
      </div>
    </div>
  );
};

export default EditPhaseModal;
